import { useEffect, useState, type FormEvent } from "react";
import { useLocation } from "react-router-dom";

import {
  useCalendar,
  useDeleteBankHoliday,
  useDeleteRole,
  useExtensionInfo,
  usePatchYearSettings,
  useSaveBankHoliday,
  useSaveRole,
  useSaveCalendar,
  useSaveRules,
  useSettingsPage,
} from "../api/hooks";
import type { SettingsPage as Data } from "../api/types";
import { AccountSettings } from "../components/AccountSettings";
import { errorText, useFlash } from "../components/Flash";
import { LoadError, Loading, useYear } from "../components/Layout";
import { dlong, dshort, money, num, toNum } from "../lib/format";

export function SettingsPage() {
  const year = useYear();
  const q = useSettingsPage(year);
  const { hash } = useLocation();
  useEffect(() => {
    if (hash && q.data) document.getElementById(hash.slice(1))?.scrollIntoView();
  }, [hash, q.data]);
  if (q.isPending) return <Loading />;
  if (q.error) return <LoadError error={q.error} />;
  return (
    <>
      <div className="page-head">
        <h1>Settings</h1>
      </div>
      <AccountSettings />
      <ExpensesSwitch data={q.data} />
      <div className="two-col">
        <RulesForm data={q.data} />
        <BankHolidays data={q.data} />
      </div>
      <div className="two-col" style={{ marginTop: "1.25rem" }}>
        <YearForm key={`y${year}`} data={q.data} />
        <Roles data={q.data} />
      </div>
      <WorkCalendar />
      <BrowserExtension />
    </>
  );
}

/** Callbacks shared by every form: flash success, flash failure. */
function useNotify() {
  const flash = useFlash();
  return (ok?: string) => ({
    onSuccess: () => ok && flash(ok),
    onError: (err: unknown) => flash(errorText(err), "error"),
  });
}

const optional = (v: string) => (v.trim() === "" ? null : toNum(v));

/** Expenses on or off. Off suits people who only use the app to track DOILs; their expense data is kept. */
function ExpensesSwitch({ data }: { data: Data }) {
  const save = useSaveRules();
  const notify = useNotify();
  const on = data.rules.expenses_enabled;
  return (
    <div className="panel" style={{ marginBottom: "1.25rem" }}>
      <h2>Expenses</h2>
      <label className="check">
        <input
          type="checkbox"
          checked={on}
          disabled={save.isPending}
          onChange={(e) =>
            save.mutate(
              { ...data.rules, expenses_enabled: e.target.checked },
              notify(e.target.checked ? "Expenses turned on." : "Expenses turned off."),
            )
          }
        />{" "}
        Track expenses on jobs
      </label>
      <p className="small muted" style={{ margin: ".4rem 0 0" }}>
        Turn this off if you only want to track days, DOILs and holidays. Jobs stay, but mileage, meals, HMRC
        rates and totals are hidden. Nothing is deleted: turn it back on to see them again. With a work
        calendar, its jobs are added to your jobs list by themselves once they've finished.
      </p>
    </div>
  );
}

function YearForm({ data }: { data: Data }) {
  const ys = data.year_settings;
  const year = data.year;
  const expenses = data.rules.expenses_enabled;
  const [f, setF] = useState({
    mileage_rate: String(ys.mileage_rate),
    meal_5h: String(ys.meal_5h),
    meal_10h: String(ys.meal_10h),
    meal_15h: String(ys.meal_15h),
    meal_u15h: String(ys.meal_u15h),
    hotel_night_rate: String(ys.hotel_night_rate),
    holiday_allowance: num(ys.holiday_allowance),
    holiday_carry_override: ys.holiday_carry_override === null ? "" : num(ys.holiday_carry_override),
    doil_start_override: ys.doil_start_override === null ? "" : num(ys.doil_start_override),
  });
  const patch = usePatchYearSettings(year);
  const notify = useNotify();
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) =>
    setF((x) => ({ ...x, [k]: e.target.value }));

  const submit = (e: FormEvent) => {
    e.preventDefault();
    patch.mutate(
      {
        mileage_rate: toNum(f.mileage_rate),
        meal_5h: toNum(f.meal_5h),
        meal_10h: toNum(f.meal_10h),
        meal_15h: toNum(f.meal_15h),
        meal_u15h: toNum(f.meal_u15h),
        hotel_night_rate: toNum(f.hotel_night_rate),
        holiday_allowance: toNum(f.holiday_allowance),
        holiday_carry_override: optional(f.holiday_carry_override),
        doil_start_override: optional(f.doil_start_override),
      },
      notify(`${year} settings saved.`),
    );
  };

  const field = (k: keyof typeof f, label: string, step = "0.01", extra: object = {}) => (
    <div>
      <label htmlFor={k}>{label}</label>
      <input id={k} type="number" step={step} value={f[k]} onChange={set(k)} {...extra} />
    </div>
  );

  return (
    <form className="panel" onSubmit={submit}>
      <h2>{expenses ? `${year} rates and allowance` : `${year} allowance`}</h2>
      <p className="small muted">
        Each year keeps its own {expenses ? "rates" : "settings"}. A new year starts with the previous year's.
      </p>
      <div className="fields">
        {expenses && (
          <>
            {field("mileage_rate", "Mileage (£ per mile)", "0.01", { min: 0 })}
            {field("meal_5h", "Meal >5H (£)", "0.01", { min: 0 })}
            {field("meal_10h", "Meal >10H (£)", "0.01", { min: 0 })}
            {field("meal_15h", "Meal >15H (£)", "0.01", { min: 0 })}
            {field("meal_u15h", "Meal <15H (£)", "0.01", { min: 0 })}
            {field("hotel_night_rate", "Hotel (£ per night, 0 = not used)", "0.01", { min: 0 })}
          </>
        )}
        {field("holiday_allowance", "Holiday allowance (days)", "0.5", { min: 0 })}
        {field("holiday_carry_override", "Holiday carried in (days)", "0.5", { placeholder: "Automatic" })}
        {field("doil_start_override", "DOILs carried in", "0.5", { placeholder: "Automatic" })}
      </div>
      <p className="small muted" style={{ marginTop: ".6rem" }}>
        Leave the two "carried in" boxes empty to carry over last year's figures automatically. Fill one in
        only if your employer caps or adjusts it.
      </p>
      <div className="actions">
        <button className="btn" type="submit" disabled={patch.isPending}>
          Save {year} settings
        </button>
      </div>
    </form>
  );
}

/** The Cost Centre list on Employment Hero's timesheet form, spelt exactly as there (the extension matches on it). */
const COST_CENTRES = [
  "210 - Finance",
  "212 - Shared Services - HR",
  "215 - Shared Services - IT",
  "310 - Camera",
  "320 - Sound",
  "330 - Vision",
  "360 - Rigging",
  "370 - Unit Managers",
  "400 - Security",
  "405 - Local Management",
  "410 - Co-ordination",
  "430 - Transport",
  "440 - Maintenance",
  "450 - Reception",
  "470 - Base",
  "815 - RF",
  "TFC",
];

function RulesForm({ data }: { data: Data }) {
  const r = data.rules;
  const [f, setF] = useState({
    fortnight_anchor: r.fortnight_anchor,
    doil_limit: String(r.doil_limit),
    doil_mode: r.doil_mode,
    bh_counts_toward_limit: r.bh_counts_toward_limit,
    first_year: r.first_year === null ? "" : String(r.first_year),
    opening_doil_balance: num(r.opening_doil_balance),
    opening_holiday_carry: num(r.opening_holiday_carry),
    opening_cross_days: num(r.opening_cross_days),
    opening_cross_bh: num(r.opening_cross_bh),
    overtime_after_hours: r.overtime_after_hours === null ? "" : num(r.overtime_after_hours),
    annual_salary: r.annual_salary === null ? "" : String(r.annual_salary),
    overtime_rate: num(r.overtime_rate),
    cost_centre: r.cost_centre ?? "",
    claims_from: r.claims_from ?? "",
  });
  const salary = toNum(f.annual_salary);
  const daily = salary > 0 ? salary / 260 : null; // as the Hours page works it out (services/hours.py)
  const save = useSaveRules();
  const notify = useNotify();
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) =>
    setF((x) => ({ ...x, [k]: e.target.value }));

  const submit = (e: FormEvent) => {
    e.preventDefault();
    save.mutate(
      {
        fortnight_anchor: f.fortnight_anchor,
        doil_limit: Math.round(toNum(f.doil_limit, 10)),
        doil_mode: f.doil_mode,
        bh_counts_toward_limit: f.bh_counts_toward_limit,
        first_year: f.first_year.trim() ? Math.round(toNum(f.first_year)) : null,
        opening_doil_balance: toNum(f.opening_doil_balance),
        opening_holiday_carry: toNum(f.opening_holiday_carry),
        opening_cross_days: toNum(f.opening_cross_days),
        opening_cross_bh: toNum(f.opening_cross_bh),
        overtime_after_hours: f.overtime_after_hours.trim() ? toNum(f.overtime_after_hours) : null,
        annual_salary: salary > 0 ? salary : null,
        overtime_rate: toNum(f.overtime_rate, 1) || 1,
        cost_centre: f.cost_centre.trim(),
        claims_from: f.claims_from,
      },
      notify("Settings saved."),
    );
  };

  return (
    <form className="panel" onSubmit={submit}>
      <h2>DOIL rules</h2>
      <div className="fields">
        <div>
          <label htmlFor="fortnight_anchor">A fortnight starts on</label>
          <input
            id="fortnight_anchor"
            type="date"
            required
            value={f.fortnight_anchor}
            onChange={set("fortnight_anchor")}
          />
        </div>
        <div>
          <label htmlFor="doil_limit">Days allowed per fortnight</label>
          <input
            id="doil_limit"
            type="number"
            min="1"
            max="14"
            value={f.doil_limit}
            onChange={set("doil_limit")}
          />
        </div>
        <div className="wide">
          <label htmlFor="doil_mode">DOILs for days over the limit</label>
          <select id="doil_mode" value={f.doil_mode} onChange={set("doil_mode")}>
            <option value="per_day">One for each day over</option>
            <option value="one">One per fortnight, however far over</option>
          </select>
        </div>
        <div className="wide">
          <label className="check">
            <input
              type="checkbox"
              checked={f.bh_counts_toward_limit}
              onChange={(e) => setF((x) => ({ ...x, bh_counts_toward_limit: e.target.checked }))}
            />{" "}
            Worked bank holidays also count toward the limit
          </label>
        </div>
      </div>
      <h3 style={{ marginTop: "1.25rem" }}>Hours</h3>
      <div className="fields">
        <div>
          <label htmlFor="overtime_after_hours">Overtime after (hours a day)</label>
          <input
            id="overtime_after_hours"
            type="number"
            min="0.5"
            max="24"
            step="0.25"
            placeholder="Off"
            value={f.overtime_after_hours}
            onChange={set("overtime_after_hours")}
          />
        </div>
        <div>
          <label htmlFor="annual_salary">Annual salary (£)</label>
          <input
            id="annual_salary"
            type="number"
            min="0"
            step="0.01"
            placeholder="Not set"
            value={f.annual_salary}
            onChange={set("annual_salary")}
          />
        </div>
        <div>
          <label htmlFor="overtime_rate">Overtime rate, by default (× hourly rate)</label>
          <input
            id="overtime_rate"
            type="number"
            min="0.1"
            max="5"
            step="0.05"
            value={f.overtime_rate}
            onChange={set("overtime_rate")}
          />
        </div>
      </div>
      <p className="small muted" style={{ margin: ".5rem 0 0" }}>
        {daily
          ? `Daily rate ${money(daily)} (salary ÷ 260 days), hourly rate ${money(daily / 8)} (÷ 8 hours). `
          : "Add your salary to see what your overtime pays. "}
        1 = straight time, 1.5 = time and a half. A job can have its own rate, or not pay overtime (in the
        job).
      </p>
      <div className="fields" style={{ marginTop: ".75rem" }}>
        <div className="wide">
          <label htmlFor="cost_centre">Employment Hero cost centre</label>
          <select id="cost_centre" value={f.cost_centre} onChange={set("cost_centre")}>
            <option value="">Not set</option>
            {f.cost_centre && !COST_CENTRES.includes(f.cost_centre) && (
              <option value={f.cost_centre}>{f.cost_centre}</option>
            )}
            {COST_CENTRES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>
      </div>
      <p className="small muted" style={{ margin: ".5rem 0 0" }}>
        The Cost Centre list from Employment Hero&apos;s timesheet form. Your timesheets on the Hours page use
        it.
      </p>
      <div className="fields" style={{ marginTop: ".75rem" }}>
        <div>
          <label htmlFor="claims_from">Concur claims tracked from</label>
          <input id="claims_from" type="date" value={f.claims_from} onChange={set("claims_from")} />
        </div>
      </div>
      <p className="small muted" style={{ margin: ".5rem 0 0" }}>
        Jobs that ended before this date were claimed by hand, so they show as claimed on the Jobs page. Later
        ones are claimed with the browser extension, which keeps track.
      </p>
      <h3 style={{ marginTop: "1.25rem" }}>Starting point</h3>
      <p className="small muted">
        Balances at the start of your first year in this app. Later years carry over automatically.
      </p>
      <div className="fields">
        <div>
          <label htmlFor="first_year">First year</label>
          <input id="first_year" type="number" value={f.first_year} onChange={set("first_year")} />
        </div>
        <div>
          <label htmlFor="opening_doil_balance">DOILs carried in</label>
          <input
            id="opening_doil_balance"
            type="number"
            step="0.5"
            value={f.opening_doil_balance}
            onChange={set("opening_doil_balance")}
          />
        </div>
        <div>
          <label htmlFor="opening_holiday_carry">Holiday days carried in</label>
          <input
            id="opening_holiday_carry"
            type="number"
            step="0.5"
            value={f.opening_holiday_carry}
            onChange={set("opening_holiday_carry")}
          />
        </div>
        <div>
          <label htmlFor="opening_cross_days">Days worked in the New Year fortnight before 1 Jan</label>
          <input
            id="opening_cross_days"
            type="number"
            min="0"
            max="14"
            value={f.opening_cross_days}
            onChange={set("opening_cross_days")}
          />
        </div>
        <div>
          <label htmlFor="opening_cross_bh">…of which bank holidays</label>
          <input
            id="opening_cross_bh"
            type="number"
            min="0"
            max="5"
            value={f.opening_cross_bh}
            onChange={set("opening_cross_bh")}
          />
        </div>
      </div>
      <div className="actions">
        <button className="btn" type="submit" disabled={save.isPending}>
          Save rules
        </button>
      </div>
    </form>
  );
}

function Roles({ data }: { data: Data }) {
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const save = useSaveRole();
  const del = useDeleteRole();
  const notify = useNotify();
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const c = code.trim();
    save.mutate(
      { code: c, name: name.trim() },
      {
        ...notify(),
        onSuccess: () => {
          setCode("");
          setName("");
        },
      },
    );
  };
  return (
    <div className="panel" id="roles">
      <h2>Roles</h2>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Code</th>
              <th>Name</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {data.roles.map((r) => (
              <tr key={r.code}>
                <td>{r.code}</td>
                <td>{r.name}</td>
                <td className="num">
                  <button
                    className="icon-btn"
                    aria-label={`Delete ${r.name}`}
                    onClick={() => del.mutate(r.code, notify())}
                  >
                    ×
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <form onSubmit={submit} className="fields" style={{ marginTop: "1rem" }}>
        <div>
          <label htmlFor="r-code">Code</label>
          <input id="r-code" maxLength={6} required value={code} onChange={(e) => setCode(e.target.value)} />
        </div>
        <div>
          <label htmlFor="r-name">Name</label>
          <input id="r-name" required value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div style={{ alignSelf: "end" }}>
          <button className="btn quiet" type="submit">
            Add role
          </button>
        </div>
      </form>
    </div>
  );
}

function BankHolidays({ data }: { data: Data }) {
  const [f, setF] = useState({ date: "", name: "", kind: "add" as "add" | "remove" });
  const save = useSaveBankHoliday();
  const del = useDeleteBankHoliday();
  const notify = useNotify();
  const submit = (e: FormEvent) => {
    e.preventDefault();
    save.mutate(
      { date: f.date, name: f.name.trim() || null, kind: f.kind },
      { ...notify(), onSuccess: () => setF({ date: "", name: "", kind: "add" }) },
    );
  };
  return (
    <div className="panel" id="bank-holidays">
      <h2>Bank holidays {data.year}</h2>
      <p className="small muted">
        England and Wales holidays are worked out automatically. Add one-offs, or remove a moved one.
      </p>
      <div className="table-wrap">
        <table>
          <tbody>
            {data.bank_holidays.map((b) => (
              <tr key={b.date}>
                <td>{dshort(b.date)}</td>
                <td>{b.name}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {data.overrides.length > 0 && (
        <>
          <h3 style={{ marginTop: "1rem" }}>Your changes</h3>
          <div className="table-wrap">
            <table>
              <tbody>
                {data.overrides.map((o) => (
                  <tr key={o.date}>
                    <td>{dlong(o.date)}</td>
                    <td>
                      {o.kind === "add" ? "Added" : "Removed"}
                      {o.name && `: ${o.name}`}
                    </td>
                    <td className="num">
                      <button
                        className="icon-btn"
                        aria-label="Undo"
                        onClick={() => del.mutate(o.date, notify())}
                      >
                        ×
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      <form onSubmit={submit} className="fields" style={{ marginTop: "1rem" }}>
        <div>
          <label htmlFor="bh-date">Date</label>
          <input
            id="bh-date"
            type="date"
            required
            value={f.date}
            onChange={(e) => setF({ ...f, date: e.target.value })}
          />
        </div>
        <div>
          <label htmlFor="bh-name">Name</label>
          <input
            id="bh-name"
            placeholder="e.g. Coronation"
            value={f.name}
            onChange={(e) => setF({ ...f, name: e.target.value })}
          />
        </div>
        <div>
          <label htmlFor="bh-kind">Change</label>
          <select
            id="bh-kind"
            value={f.kind}
            onChange={(e) => setF({ ...f, kind: e.target.value as "add" | "remove" })}
          >
            <option value="add">Add holiday</option>
            <option value="remove">Remove holiday</option>
          </select>
        </div>
        <div style={{ alignSelf: "end" }}>
          <button className="btn quiet" type="submit">
            Save
          </button>
        </div>
      </form>
    </div>
  );
}

/** The work calendar link (an iCal feed). Its days fill the planner; jobs and days marked by hand win. */
function WorkCalendar() {
  const q = useCalendar();
  const save = useSaveCalendar();
  const flash = useFlash();
  const [url, setUrl] = useState<string | null>(null);
  const value = url ?? q.data?.url ?? "";
  const s = q.data;

  const submit = (link: string | null) =>
    save.mutate(link, {
      onSuccess: (r) => {
        setUrl(null);
        // While it's being read, an error shown would be the last attempt's, so say it's reading instead.
        if (!r.url) flash("Calendar removed.");
        else if (r.syncing) flash("Calendar saved. Reading it now; this can take a minute.");
        else if (r.error) flash(r.error, "error");
        else flash(`Calendar saved: ${r.days} days read.`);
      },
      onError: (err) => flash(errorText(err), "error"),
    });
  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    submit(value.trim() || null);
  };
  const remove = () => {
    if (
      window.confirm(
        "Remove the work calendar? Days it filled in on the planner are removed too; your own marks and jobs stay.",
      )
    )
      submit(null);
  };

  return (
    <form className="panel" id="calendar" style={{ marginTop: "1.25rem" }} onSubmit={onSubmit}>
      <h2>Work calendar</h2>
      <p className="small muted" style={{ marginTop: 0 }}>
        Fills the planner from your work calendar&apos;s iCal link (it&apos;s read about once an hour). Off
        and Request Off are days off, Day In Lieu is a DOIL used, Prep and Shop/Base are base days, Vacation
        is a holiday, and anything else is a day on site. Days you mark on the planner and jobs you add always
        win.
      </p>
      <label htmlFor="calendar-url">Calendar link</label>
      <input
        id="calendar-url"
        type="url"
        inputMode="url"
        autoComplete="off"
        spellCheck={false}
        placeholder="https://…"
        value={value}
        onChange={(e) => setUrl(e.target.value)}
      />
      <p className="small muted" style={{ margin: ".4rem 0 0" }}>
        Treat this link like a password: anyone with it can read your calendar.
      </p>
      {s?.url && (
        <p className="small" style={{ margin: ".6rem 0 0" }}>
          {s.syncing ? (
            <span role="status">Reading the calendar… this can take a minute.</span>
          ) : s.error ? (
            <span className="error-text">{s.error}</span>
          ) : (
            <>
              {s.days} days saved
              {s.first && s.last ? ` (${dlong(s.first)} – ${dlong(s.last)})` : ""}
              {s.synced_at &&
                `, updated ${new Date(s.synced_at).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" })}`}
              .
            </>
          )}
        </p>
      )}
      <div className="actions">
        <button className="btn" type="submit" disabled={save.isPending || (!value.trim() && !s?.url)}>
          {save.isPending ? "Reading calendar…" : "Save"}
        </button>
        {s?.url && (
          <button className="btn danger" type="button" onClick={remove} disabled={save.isPending}>
            Remove calendar
          </button>
        )}
      </div>
    </form>
  );
}

/** The browser extension (Employment Hero and Concur): a download set up for this Work Tracker, and how to install it. */
function BrowserExtension() {
  const q = useExtensionInfo();
  if (!q.data?.available) return null;
  const href = `/api/extension.zip?site=${encodeURIComponent(window.location.origin)}`;
  return (
    <section className="panel extension-panel" id="extension" style={{ marginTop: "1.25rem" }}>
      <h2>Browser extension</h2>
      <p className="small muted" style={{ marginTop: 0 }}>
        Fills in and saves your timesheets in Employment Hero from your hours here, for Microsoft Edge or
        Google Chrome on a computer. It uses your cost centre (above) and each day&apos;s break times, and is
        already set up for this Work Tracker. On Concur it adds your jobs&apos; expenses to expense claims
        (give each job its Concur job code), with receipts from a folder you choose on the extension&apos;s
        setup page.
      </p>
      <a className="btn" href={href} download>
        Download the extension (version {q.data.version})
      </a>
      <div className="two-col" style={{ marginTop: "1rem" }}>
        <div>
          <h3>Install</h3>
          <ol className="steps">
            <li>
              Download it, then right-click the zip and choose <b>Extract All</b>. Keep the folder somewhere
              safe, like Documents: the browser runs the extension from it.
            </li>
            <li>
              <b>Edge:</b> go to <code>edge://extensions</code> and turn on <b>Developer mode</b> (in the menu
              on the left).
              <br />
              <b>Chrome:</b> go to <code>chrome://extensions</code> and turn on <b>Developer mode</b> (top
              right).
            </li>
            <li>
              Click <b>Load unpacked</b> and choose the <b>work-tracker-extension</b> folder you unzipped.
            </li>
            <li>
              Sign in to Work Tracker in that browser, then open Employment Hero: the <b>Work Tracker</b>{" "}
              button is at the bottom right. Tick days and press <b>Submit</b>.
            </li>
          </ol>
        </div>
        <div>
          <h3>Updating</h3>
          <p className="small">
            When the extension says a new version is available, download it again here, extract it over the
            old folder (replace the files), then click the reload button on the extension in{" "}
            <code>edge://extensions</code> or <code>chrome://extensions</code>.
          </p>
          <p className="small muted">
            If Developer mode is greyed out, your company&apos;s browser settings only allow extensions from
            the store.
          </p>
        </div>
      </div>
    </section>
  );
}
